/**
 * Shared-realm onboarding (user-service, gateway prefix /api/v1/users).
 *
 * - `GET  /api/v1/users/me/karexia-access` — what a signed-in `core` account with
 *   no Karexia role can do next: pending org invitations (priority) and whether
 *   it may create its own workspace.
 * - `POST /api/v1/users/me/workspace` — self-serve "Create my workspace" (new
 *   tenant, OWNER role, terms/privacy proof, optional CASL/Law-25 express
 *   marketing consent). Idempotent server-side: a double submit replays the
 *   same tenant.
 * - `POST /api/v1/users/sso/discover` — PUBLIC company-SSO discovery: work email
 *   -> the tenant's IdP broker alias (or null). Rate limited, latency padded.
 *
 * The gateway lets a no-access account reach exactly the first two paths.
 */

import { ApiError, api } from './client';

export interface PendingInvitation {
  accept_path: string;
  org_role: string;
  expires_at: string | null;
}

export interface KarexiaAccessStatus {
  karexia_access: boolean;
  self_serve_available: boolean;
  email: string | null;
  email_verified: boolean;
  pending_invitation_count: number;
  pending_invitations: PendingInvitation[];
  signup: { status: string; org_name: string; tenant_id: string } | null;
  terms_version: string;
  privacy_version: string;
}

export interface CreateWorkspaceInput {
  org_name: string;
  accept_terms: boolean;
  accept_privacy: boolean;
  marketing_opt_in: boolean;
  /** The exact (translated) checkbox wording shown — the consent proof. */
  marketing_wording?: string | null;
  locale?: string | null;
  source_url?: string | null;
  skip_pending_invitations?: boolean;
}

export interface CreateWorkspaceResult {
  status: string;
  tenant_id: string;
  org_name: string;
  reissue_required: boolean;
  replayed: boolean;
  marketing_consent_status: string;
}

/** Error codes the workspace endpoint returns (`{error, message}` bodies). */
export const WORKSPACE_ERROR_CODES = [
  'pending_invitation',
  'already_has_workspace',
  'invalid_org_name',
  'terms_not_accepted',
  'marketing_wording_required',
  'rate_limited',
  'self_serve_unavailable',
  'self_serve_disabled',
  'tenant_provisioning_failed',
  'identity_provider_unavailable',
] as const;
export type WorkspaceErrorCode = (typeof WORKSPACE_ERROR_CODES)[number] | 'network' | 'unknown';

export const ORG_NAME_MIN = 2;
export const ORG_NAME_MAX = 100;

/** Normalised organisation name (collapsed whitespace), as the server stores it. */
export function normaliseOrgName(value: string): string {
  return value.split(/\s+/).filter(Boolean).join(' ');
}

export function isValidOrgName(value: string): boolean {
  const n = normaliseOrgName(value);
  return n.length >= ORG_NAME_MIN && n.length <= ORG_NAME_MAX;
}

/** Map any thrown error to one of the documented workspace error codes. */
export function workspaceErrorCode(err: unknown): WorkspaceErrorCode {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'network';
    if (err.status === 429) return 'rate_limited';
    const body = err.body as { error?: unknown } | null;
    const code = body && typeof body === 'object' ? body.error : undefined;
    if (typeof code === 'string' && (WORKSPACE_ERROR_CODES as readonly string[]).includes(code)) {
      return code as WorkspaceErrorCode;
    }
  }
  return 'unknown';
}

export function getKarexiaAccessStatus(): Promise<KarexiaAccessStatus> {
  return api.get<KarexiaAccessStatus>('/api/v1/users/me/karexia-access');
}

export function createWorkspace(input: CreateWorkspaceInput): Promise<CreateWorkspaceResult> {
  return api.post<CreateWorkspaceResult>('/api/v1/users/me/workspace', input);
}

export interface SsoDiscovery {
  alias: string | null;
}

export function discoverCompanySso(email: string): Promise<SsoDiscovery> {
  return api.post<SsoDiscovery>('/api/v1/users/sso/discover', { email: email.trim() });
}
